import { deepClone } from '@/utils/clone';
import { jsonChunks, equalJsonChunks } from '@/utils/jsonChunks';
import type { ChipModelDto, ProjectDto } from '@/schemas/chip';

export class ProjectSaveConflict extends Error {
  constructor(public readonly chips: string[]) {
    super(
      chips.length
        ? `Another window changed: ${chips.join(', ')}. Your work is still open here.`
        : 'Another window replaced or deleted this project. Your work is still open here.',
    );
    this.name = 'ProjectSaveConflict';
  }
}

export type SessionChip = Pick<ChipModelDto, 'id' | 'name'> & {
  chipInstanceDtos: { chipId: ChipModelDto['id'] }[];
};
export type SessionProject<C extends SessionChip> = Omit<ProjectDto, 'chips'> & {
  chips: C[];
};
export interface SnapshotOperations<C extends SessionChip> {
  cloneProject(project: SessionProject<C>): SessionProject<C>;
  cloneChip(chip: C): C;
  same(a: C | undefined, b: C | undefined): Generator<void, boolean>;
}
const dtoOperations: SnapshotOperations<ChipModelDto> = {
  cloneProject: deepClone,
  cloneChip: deepClone,
  same: sameSnapshotSteps,
};
const index = <C extends SessionChip>(project: SessionProject<C> | null) =>
  new Map(project?.chips.map((chip) => [chip.id, chip]) ?? []);
// Public callers retain private immutable snapshots. Worker messages already own
// their structured-cloned DTOs; comparing on demand avoids permanent JSON copies.
type Fingerprint = readonly string[];
type Snapshot = ChipModelDto;
function* sameSnapshotSteps(
  a: Snapshot | undefined,
  b: Snapshot | undefined,
): Generator<void, boolean> {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  const left = jsonChunks(a, { canonical: true });
  const right = jsonChunks(b, { canonical: true });
  while (true) {
    const x = left.next(),
      y = right.next();
    if (x.done || y.done) return x.done === y.done;
    if (x.value !== y.value) return false;
    yield;
  }
}
const equalValues = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object')
    return false;
  if (Array.isArray(a))
    return (
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((value, i) => equalValues(value, b[i]))
    );
  if (Array.isArray(b)) return false;
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(b, key) && equalValues(a[key], b[key]),
    )
  );
};
/** Compare canonical chunks. The string branch accepts older internal fingerprints. */
export function sameProjectFingerprint(
  a: string | Fingerprint | undefined,
  b: string | Fingerprint | undefined,
): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  if (typeof a !== 'string' || typeof b !== 'string')
    return typeof a !== 'string' && typeof b !== 'string' && equalJsonChunks(a, b);
  if (a.length !== b.length) return false;
  let inString = false;
  let escaped = false;
  let cursor = 0;
  for (; cursor < a.length && a.charCodeAt(cursor) === b.charCodeAt(cursor); cursor++) {
    const char = a.charCodeAt(cursor);
    if (!inString) {
      if (char === 34) inString = true;
    } else if (escaped) escaped = false;
    else if (char === 92) escaped = true;
    else if (char === 34) inString = false;
  }
  // Canonical equal values can first differ only in an object's key order.
  // Structural/primitive differences and unequal string values cannot be fixed
  // by reordering keys, so avoid parsing two complete runtime snapshots.
  if (!inString) return false;
  for (; cursor < a.length; cursor++) {
    const char = a.charCodeAt(cursor);
    if (escaped) escaped = false;
    else if (char === 92) escaped = true;
    else if (char === 34) {
      if (a.charCodeAt(cursor + 1) !== 58) return false;
      return equalValues(JSON.parse(a), JSON.parse(b));
    }
  }
  return false;
}
function* snapshotSteps<C extends SessionChip>(
  project: SessionProject<C> | null,
  owned: boolean,
  operations: SnapshotOperations<C>,
): Generator<void, Map<ChipModelDto['id'], C>> {
  const result = new Map<ChipModelDto['id'], C>();
  for (const chip of project?.chips ?? []) {
    result.set(chip.id, owned ? chip : operations.cloneChip(chip));
    yield;
  }
  return result;
}
function finish<T>(steps: Generator<void, T>): T {
  let result = steps.next();
  while (!result.done) result = steps.next();
  return result.value;
}
/** Internal persistence handle; merged results are read-only storage inputs.
 * Owns a call-time snapshot, never a cache of a caller's mutable DTO. */
export interface PreparedProjectSave<C extends SessionChip = ChipModelDto> {
  merge(stored: SessionProject<C> | null): SessionProject<C>;
  mergeSteps(stored: SessionProject<C> | null): Generator<void, SessionProject<C>>;
  didSave(saved: SessionProject<C>): void;
  didSaveSteps(saved: SessionProject<C>): Generator<void>;
}

/** Separate local and stored baselines account for graph hydration changing DTO identities. */
export class ProjectEditSession<C extends SessionChip = ChipModelDto> {
  private local: Map<ChipModelDto['id'], C>;
  private stored: Map<ChipModelDto['id'], C>;
  private storedMainId: SessionProject<C>['mainChipId'] | undefined;

  constructor(
    local: SessionProject<C>,
    stored: SessionProject<C> | null,
    deferFingerprints = false,
    ownedInputs = false,
    private readonly operations: SnapshotOperations<C> = dtoOperations as unknown as SnapshotOperations<C>,
  ) {
    this.local = deferFingerprints
      ? new Map()
      : finish(snapshotSteps(local, ownedInputs, this.operations));
    this.stored = deferFingerprints
      ? new Map()
      : finish(snapshotSteps(stored, ownedInputs, this.operations));
    this.storedMainId = stored?.mainChipId;
  }

  static *prepare<C extends SessionChip = ChipModelDto>(
    local: SessionProject<C>,
    stored: SessionProject<C> | null,
    ownedInputs = false,
    operations: SnapshotOperations<C> = dtoOperations as unknown as SnapshotOperations<C>,
  ): Generator<void, ProjectEditSession<C>> {
    const session = new ProjectEditSession(local, stored, true, ownedInputs, operations);
    session.local = yield* snapshotSteps(local, ownedInputs, operations);
    session.stored = yield* snapshotSteps(stored, ownedInputs, operations);
    return session;
  }

  prepareSave(local: SessionProject<C>, ownedInput = false): PreparedProjectSave<C> {
    // Capture before joining the FIFO queue. The caller can mutate its DTO as
    // soon as saveProjectChanges returns without changing this save's meaning.
    const snapshot = ownedInput ? local : this.operations.cloneProject(local);
    const session = this;
    let captured: Map<ChipModelDto['id'], C> | undefined;
    let comparison:
      | { baseline: Map<ChipModelDto['id'], C>; changed: Set<ChipModelDto['id']> }
      | undefined;
    function* localFingerprints(): Generator<void, Map<ChipModelDto['id'], C>> {
      // Assign only after a complete pass; interrupted generators cannot expose
      // a partially prepared map. Storage retries reuse only this owned input.
      captured ??= yield* snapshotSteps(snapshot, true, session.operations);
      return captured;
    }
    return {
      merge(stored) {
        return finish(this.mergeSteps(stored));
      },
      *mergeSteps(stored) {
        comparison = undefined;
        session.validateStoredProject(stored);
        const next = yield* localFingerprints();
        const baseline = session.local;
        const changed = new Set<ChipModelDto['id']>();
        const merged = yield* session.mergePreparedSteps(snapshot, stored, next, changed);
        comparison = { baseline, changed };
        return merged;
      },
      didSave(saved) {
        finish(this.didSaveSteps(saved));
      },
      *didSaveSteps(saved) {
        const next = yield* localFingerprints();
        // The committed merge already compared this immutable input. Reuse only
        // while the baseline is unchanged; interleaved public callers fall back.
        yield* session.acknowledgeSteps(next, saved, comparison);
      },
    };
  }

  merge(local: SessionProject<C>, stored: SessionProject<C> | null): SessionProject<C> {
    return finish(this.mergeSteps(local, stored));
  }

  *mergeSteps(
    local: SessionProject<C>,
    stored: SessionProject<C> | null,
  ): Generator<void, SessionProject<C>> {
    this.validateStoredProject(stored);
    return yield* this.mergePreparedSteps(
      local,
      stored,
      yield* snapshotSteps(local, false, this.operations),
    );
  }

  private validateStoredProject(stored: SessionProject<C> | null) {
    if (this.storedMainId && (!stored || stored.mainChipId !== this.storedMainId))
      throw new ProjectSaveConflict([]);
    if (!this.storedMainId && stored) throw new ProjectSaveConflict([]);
  }

  private *mergePreparedSteps(
    local: SessionProject<C>,
    stored: SessionProject<C> | null,
    nextFingerprints: Map<ChipModelDto['id'], C>,
    changed?: Set<ChipModelDto['id']>,
  ): Generator<void, SessionProject<C>> {
    const incoming = index(local);
    const current = index(stored);
    const merged = new Map(stored ? current : incoming);
    const conflicts: string[] = [];
    for (const id of new Set([...this.local.keys(), ...incoming.keys()])) {
      yield;
      const next = incoming.get(id);
      if (yield* this.operations.same(nextFingerprints.get(id), this.local.get(id)))
        continue;
      changed?.add(id);
      // Unchanged local chips keep the stored version without comparison. Avoid
      // serializing their potentially large graphs just to discard the result.
      const currentChip = current.get(id);
      const storedFingerprint = currentChip;
      if (
        !(yield* this.operations.same(storedFingerprint, this.stored.get(id))) &&
        !(yield* this.operations.same(storedFingerprint, nextFingerprints.get(id)))
      ) {
        conflicts.push(next?.name ?? current.get(id)?.name ?? 'a deleted chip');
        continue;
      }
      if (next) merged.set(id, next);
      else merged.delete(id);
    }
    if (conflicts.length) throw new ProjectSaveConflict(conflicts);
    // Reject missing definitions. Cycles are valid in experimental projects;
    // graph expansion cuts them at a deterministic boundary on load.
    for (const chip of merged.values()) {
      for (const id of new Set(
        chip.chipInstanceDtos.map((instance) => instance.chipId),
      )) {
        if (!merged.has(id as ChipModelDto['id']))
          throw new ProjectSaveConflict([chip.name]);
      }
    }
    return { ...local, chips: [...merged.values()] };
  }

  didSave(local: SessionProject<C>, saved: SessionProject<C>) {
    finish(this.didSaveSteps(local, saved));
  }

  *didSaveSteps(local: SessionProject<C>, saved: SessionProject<C>): Generator<void> {
    yield* this.acknowledgeSteps(
      yield* snapshotSteps(local, false, this.operations),
      saved,
    );
  }

  private *acknowledgeSteps(
    nextLocal: Map<ChipModelDto['id'], C>,
    saved: SessionProject<C>,
    comparison?: {
      baseline: Map<ChipModelDto['id'], C>;
      changed: ReadonlySet<ChipModelDto['id']>;
    },
  ): Generator<void> {
    for (const id of new Set([...this.local.keys(), ...nextLocal.keys()])) {
      yield;
      // Do not acknowledge remote changes that this view has never loaded. A
      // later edit to that chip must still conflict rather than overwrite it.
      if (
        (comparison?.baseline === this.local
          ? comparison.changed.has(id)
          : !(yield* this.operations.same(nextLocal.get(id), this.local.get(id)))) ||
        !this.storedMainId
      ) {
        const fingerprint = nextLocal.get(id);
        if (fingerprint !== undefined) this.stored.set(id, fingerprint);
        else this.stored.delete(id);
      }
    }
    this.local = nextLocal;
    this.storedMainId = saved.mainChipId;
  }
}
